import axiosRequest from "@/utils/axios-utils";

/*
###############################
 01:---> GET TRADE-IN CONTENT,
 02:---> GET A CMS COMPONENT'S HTML,
 03:---> APPLY / REMOVE THE DISCOUNT
###############################
*/

// 01- ============= content by sku
// A SKU with no service still comes back, with service_id null — presence in
// the array is not eligibility.
const getTradeInContent = async (skus, components) => {
  const query = `skus=${encodeURIComponent(skus.join(","))}`;
  const filter = components
    ? `&components=${encodeURIComponent(components)}`
    : "";

  let config = {
    method: "get",
    url: `/trade-in/content?${query}${filter}`,
  };

  try {
    const req = await axiosRequest(config);
    return req;
  } catch (error) {
    return error;
  }
};

// 02- ============= a component's HTML
// `components.popup.url` serves a body-only fragment. `credentials: "omit"`
// keeps Magento's PHPSESSID out of the shopper's cookie jar.
const getTradeInComponentHtml = async (url) => {
  const res = await fetch(url, { credentials: "omit" });
  if (!res.ok) throw new Error(`Trade-in content ${res.status}`);
  return res.text();
};

/**
 * WORKAROUND (AL-263). The `quote` cookie can hold the literal string
 * "undefined" (`setCookie` serialises undefined that way), and can exist
 * twice with different paths — `getCookie` then returns whichever comes last.
 * Both look like a valid cart id and end as "No such entity with cartId".
 *
 * This only makes trade-in read correctly. It does not stop the bad value
 * being written, and cart, checkout, merge, reorder and payment still break
 * on it. Real fix is in `utils/cookies/quoteCookie.js` + the writers, as one
 * change across those screens — tracked separately. Drop this when it lands.
 */
const JUNK_QUOTE_VALUES = ["", "undefined", "null", "NaN"];

const readQuoteCookie = () => {
  if (typeof document === "undefined") return null;

  for (const entry of document.cookie.split("; ")) {
    const separator = entry.indexOf("=");
    if (separator < 0 || entry.slice(0, separator).trim() !== "quote") continue;

    const value = entry.slice(separator + 1).trim();
    if (value && !JUNK_QUOTE_VALUES.includes(value)) return value;
  }

  return null;
};

// 03- ============= apply / remove
// Same payload as the legacy /customer/acReplacementDiscount.
const applyAndRemoveTradeInDiscount = async (itemId, action) => {
  const quoteId = readQuoteCookie();

  // Nothing usable to send — fail here instead of asking Magento for the same
  // answer. The caller already shows a failure toast.
  if (!quoteId) {
    return { data: null, error: "Missing quote cookie", status: 400 };
  }

  const isMask = quoteId.match(/^\d+$/) ? false : true;

  const data = JSON.stringify({
    itemId,
    quoteId,
    action,
    isMask,
  });

  let config = {
    method: "post",
    url: `/trade-in/discount`,
    headers: {},
    data,
  };

  try {
    const req = await axiosRequest(config);
    return req;
  } catch (error) {
    return error;
  }
};

// export all functions
export {
  getTradeInContent,
  getTradeInComponentHtml,
  applyAndRemoveTradeInDiscount,
};
